import { Link } from "react-router";
import { ArrowRight, ExternalLink, LifeBuoy, Mail } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { FaqAccordion } from "../_components/faq-accordion";
import { usePortalContent } from "../use-portal-content";
import { usePortalFaq } from "../use-portal-faq";

// Phase 7 sub-bucket 8 — port of /app/portal/support/page.tsx.
//
// All backend reads were already in place: `usePortalContent` (sub-bucket 1)
// returns the resolved support_links content; `usePortalFaq` returns the
// FAQ corpus. No new endpoints needed. The FaqAccordion component from the
// dashboard is reused verbatim with `showViewAll={false}` so the support
// page itself doesn't link to itself.

const SUPPORT_EMAIL = "partnerships@bookboost.io";

export function SupportPage() {
  const contentQuery = usePortalContent();
  const faqQuery = usePortalFaq();

  const linksContent = contentQuery.data?.support_links?.resolved?.content ?? null;
  const faqItems = faqQuery.data ?? [];

  return (
    <div className="space-y-7">
      <header>
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Support
        </p>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
          We&apos;re here to help
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Browse the FAQ for quick answers, or reach out to the partnerships team directly.
        </p>
      </header>

      <section className="rounded-2xl border bg-gradient-to-br from-accent/5 via-card to-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <LifeBuoy className="h-4 w-4 text-accent" />
              <h2 className="font-display text-lg font-semibold tracking-tight">
                {linksContent?.heading ?? "Need help?"}
              </h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Email the partnerships team for anything not covered below.
            </p>
          </div>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Support request from partner portal")}`}
            className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3.5 py-2 text-sm font-medium text-background transition hover:bg-foreground/90"
          >
            <Mail className="h-3.5 w-3.5" />
            Email {SUPPORT_EMAIL}
          </a>
        </div>

        {contentQuery.isLoading ? (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : linksContent && linksContent.links.length > 0 ? (
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {linksContent.links.map((link) => (
              <li key={`${link.href}-${link.label}`}>
                <a
                  href={link.href}
                  target={link.href.startsWith("http") ? "_blank" : undefined}
                  rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="group flex h-full flex-col rounded-lg border bg-card p-4 transition hover:border-accent"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{link.label}</span>
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground transition group-hover:text-accent" />
                  </div>
                  {link.description ? (
                    <p className="mt-1 text-xs text-muted-foreground">{link.description}</p>
                  ) : null}
                </a>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      {faqQuery.isLoading ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : (
        <FaqAccordion
          items={faqItems}
          groupByCategory
          showViewAll={false}
          heading="Frequently asked questions"
        />
      )}

      <section className="rounded-xl border bg-card p-6 text-sm text-muted-foreground">
        <p>
          Still stuck? Email{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="font-medium text-accent hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>{" "}
          and we&apos;ll get back to you within one business day.
        </p>
        <Link
          to="/portal/dashboard"
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
        >
          Back to dashboard
          <ArrowRight className="h-3 w-3" />
        </Link>
      </section>
    </div>
  );
}
